import Icon from '@/components/ui/AppIcon';

interface ComparisonRow {
  feature: string;
  traditional: string;
  dannyclans: string;
  advantage: boolean;
}

interface ComparisonSectionProps {
  className?: string;
}

const ComparisonSection = ({ className = '' }: ComparisonSectionProps) => {
  const comparisons: ComparisonRow[] = [
    {
      feature: "Service Model",
      traditional: "Full-service agency with creative, strategy, and execution",
      dannyclans: "Infrastructure-only platform providing compliant account access",
      advantage: true
    },
    {
      feature: "Pricing Structure",
      traditional: "Percentage of ad spend or performance-based fees",
      dannyclans: "Transparent prepaid infrastructure access fees",
      advantage: true
    },
    {
      feature: "Performance Promises",
      traditional: "ROAS guarantees, conversion rate promises, growth projections",
      dannyclans: "No performance guarantees - you control optimization",
      advantage: true
    },
    {
      feature: "Compliance Approach",
      traditional: "Reactive compliance when platform issues arise",
      dannyclans: "Proactive compliance-first architecture from day one",
      advantage: true
    },
    {
      feature: "Client Relationship",
      traditional: "Agency manages campaigns on your behalf",
      dannyclans: "You manage campaigns through our infrastructure",
      advantage: true
    },
    {
      feature: "Regulatory Framework",
      traditional: "Domestic jurisdiction with limited global flexibility",
      dannyclans: "Hong Kong SAR incorporation for optimal global compliance",
      advantage: true
    },
    {
      feature: "Service Scope",
      traditional: "Creative, strategy, media buying, reporting, optimization",
      dannyclans: "Platform access, compliance infrastructure, account management",
      advantage: false
    },
    {
      feature: "Target Client",
      traditional: "Businesses needing full marketing support",
      dannyclans: "Professional advertisers needing infrastructure only",
      advantage: false
    }
  ];

  return (
    <section className={`py-20 lg:py-28 bg-surface ${className}`}>
      <div className="mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="max-w-3xl mb-16">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-6">
            Traditional Agency vs. Infrastructure Provider
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Understanding the fundamental difference between full-service agencies and infrastructure-first platforms is critical for making the right partnership decision.
          </p>
        </div>

        <div className="bg-card rounded-sm border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="font-headline font-semibold text-left px-6 py-4 text-sm lg:text-base">
                    Feature
                  </th>
                  <th className="font-headline font-semibold text-left px-6 py-4 text-sm lg:text-base">
                    Traditional Agency
                  </th>
                  <th className="font-headline font-semibold text-left px-6 py-4 text-sm lg:text-base">
                    Danny Clans Media
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <tr
                    key={index}
                    className={`border-b border-border ${
                      index % 2 === 0 ? 'bg-card' : 'bg-surface'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {row.advantage && (
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            variant="solid"
                            className="text-success flex-shrink-0"
                          />
                        )}
                        <span className="font-headline font-semibold text-sm lg:text-base text-primary">
                          {row.feature}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-body text-sm lg:text-base text-text-secondary">
                        {row.traditional}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-body text-sm lg:text-base text-text-primary font-medium">
                        {row.dannyclans}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 p-6 bg-accent/10 border border-accent/20 rounded-sm">
          <div className="flex items-start space-x-4">
            <Icon
              name="InformationCircleIcon"
              size={24}
              variant="solid"
              className="text-accent flex-shrink-0 mt-1"
            />
            <div>
              <h4 className="font-headline font-semibold text-lg text-primary mb-2">
                Key Insight
              </h4>
              <p className="font-body text-base text-text-secondary leading-relaxed">
                We intentionally limit our scope to infrastructure. This isn't a weakness - it's our competitive advantage. By focusing exclusively on compliance and platform access, we deliver institutional-grade reliability that full-service agencies cannot match.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;